import React, { useState, useRef, useEffect } from 'react';
import { css } from 'glamor';
import { useQuery, useMutation } from '@apollo/client';
import { gray40 } from '../../theme';
import { H3 } from '../Typography';
import { Table, TBody, TR, TD } from '../Table';
import { Button } from '../Button';
import { PermissionOnly } from '../AdminOnly';
import { PERMISSIONS } from '../../lib/authManager';
import { StateDot } from './StateDot';
import { ReasonDialog } from './ReasonDialog';
import { FLAVOUR_COLOURS } from './constants/flavourColours';
import { OVERRIDE_REASONS } from './constants/overrideReasons';
import {
	ACCESS_STATE_DISPLAY,
	BOLLARD_POSITION_DISPLAY,
	OUTCOME_MESSAGE,
	UNKNOWN_OUTCOME_MESSAGE,
} from './constants/display';
import { parklioBookingBayAccess } from '../../pages/Booking/queries';
import { parklioBookingBarrierOverrideMutation } from '../../pages/Booking/mutations';

const TONE_COLOUR = {
	success: FLAVOUR_COLOURS.success,
	danger: FLAVOUR_COLOURS.danger,
	info: gray40,
};

function RowHead(props) {
	return <td role="rowheader" className={css({ width: '30%', color: gray40 })} {...props} />;
}

// A device that is not reachable has no trustworthy position, so surface the
// link state instead of a stale Accessible/Protected label.
function bollardDisplay(reachability, position) {
	if (reachability !== 'ONLINE') return { label: 'Offline', flavour: 'danger' };
	return BOLLARD_POSITION_DISPLAY[position] ?? BOLLARD_POSITION_DISPLAY.UNKNOWN;
}

export function BayAccessSection({ bookingId }) {
	const { data, error, refetch } = useQuery(parklioBookingBayAccess, {
		variables: { bookingId },
		fetchPolicy: 'cache-and-network',
		pollInterval: 15000,
	});
	const [override, { loading: commanding }] = useMutation(
		parklioBookingBarrierOverrideMutation,
	);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [message, setMessage] = useState(null);
	const [refetching, setRefetching] = useState(false);

	const mounted = useRef(true);
	useEffect(() => {
		return () => {
			mounted.current = false;
		};
	}, []);

	const view = data?.parklioBookingBayAccess;
	if (!view) {
		if (error && !data) {
			return (
				<>
					<H3>Bay access</H3>
					<div className={css({ marginBottom: 12, color: FLAVOUR_COLOURS.danger })}>
						Could not load bay access.{' '}
						<Button mode="naked" size="small" onClick={() => refetch()}>
							Retry
						</Button>
					</div>
				</>
			);
		}
		return null;
	}

	const access = ACCESS_STATE_DISPLAY[view.accessState] ?? {
		label: view.accessState,
		flavour: 'gray',
	};
	const bollard = bollardDisplay(view.reachability, view.position);
	const carPresent = view.carPresent === true;

	const runCommand = async (action, reason) => {
		if (mounted.current) setMessage(null);
		let result;
		try {
			const res = await override({
				variables: { bookingId, action, reason: reason ?? null },
			});
			const outcome = res?.data?.parklioBookingBarrierOverride?.outcome;
			result = OUTCOME_MESSAGE[outcome] ?? UNKNOWN_OUTCOME_MESSAGE;
		} catch (err) {
			result = { text: 'The command could not be sent. Please try again.', tone: 'danger' };
		}
		if (mounted.current) setMessage(result);
		try {
			if (mounted.current) setRefetching(true);
			await refetch();
		} catch (err) {
			// Snapshot refresh is best-effort; the command already returned its outcome.
		} finally {
			if (mounted.current) setRefetching(false);
		}
	};

	return (
		<>
			<H3>Bay access</H3>
			<Table spaceBelow="medium">
				<TBody>
					<TR>
						<RowHead>Assigned bay</RowHead>
						<TD>
							{view.bayLabel}
							{view.zone ? ` · ${view.zone}` : ''}
						</TD>
					</TR>
					<TR>
						<RowHead>Access state</RowHead>
						<TD>
							<StateDot flavour={access.flavour}>{access.label}</StateDot>
						</TD>
					</TR>
					<TR>
						<RowHead>Bollard state</RowHead>
						<TD>
							<StateDot flavour={bollard.flavour}>{bollard.label}</StateDot>
							{carPresent ? (
								<span className={css({ marginLeft: 8, color: gray40 })}>· vehicle present</span>
							) : null}
						</TD>
					</TR>
				</TBody>
			</Table>
			<PermissionOnly permissions={PERMISSIONS.BOOKINGS_MANAGE}>
				<div className={css({ display: 'flex', gap: 8, marginBottom: 12 })}>
					<Button
						size="small"
						disabled={commanding || refetching}
						onClick={() => runCommand('OPEN')}
					>
						Lower bollard
					</Button>
					<Button
						size="small"
						flavour="danger"
						disabled={commanding || refetching || carPresent}
						title={carPresent ? 'A vehicle is on the bay — cannot raise' : undefined}
						onClick={() => setDialogOpen(true)}
					>
						Raise bollard
					</Button>
				</div>
				{carPresent ? (
					<div className={css({ marginBottom: 12, color: FLAVOUR_COLOURS.danger })}>
						A vehicle is on the bay — the bollard cannot be raised.
					</div>
				) : null}
				<div role="status" aria-live="polite">
					{message ? (
						<div className={css({ marginBottom: 12, color: TONE_COLOUR[message.tone] ?? gray40 })}>
							{message.text}
						</div>
					) : null}
				</div>
			</PermissionOnly>
			<ReasonDialog
				open={dialogOpen}
				title="Raise bollard"
				description="Raising the bollard blocks the bay. Select a reason to continue."
				reasons={OVERRIDE_REASONS}
				confirmLabel="Raise bollard"
				destructive
				onClose={() => setDialogOpen(false)}
				onConfirm={({ reason }) => {
					setDialogOpen(false);
					runCommand('CLOSE', reason);
				}}
			/>
		</>
	);
}
